import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  Wrench,
  Loader2,
  Play,
  Pause,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  Palette,
  AlertTriangle,
} from 'lucide-react';

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  customer: string;
  quantity: number;
  priority: string;
  status: string;
  workflow_type: string;
  source_shelf_id: string | null;
  requires_paint: boolean;
  requires_inserts: boolean;
  is_composite: boolean;
  parent_work_order_id: string | null;
  paused_at: string | null;
  resume_priority: number;
  created_at: string;
  notes: string | null;
}

interface Shelf {
  id: string;
  code: string;
  name: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

export default function AssemblyStation() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [activeWO, setActiveWO] = useState<WorkOrder | null>(null);
  const [shelves, setShelves] = useState<Shelf[]>([]);

  // Diálogo de trabajo
  const [workDialogOpen, setWorkDialogOpen] = useState(false);
  const [selectedWO, setSelectedWO] = useState<WorkOrder | null>(null);
  const [workNotes, setWorkNotes] = useState('');

  // Diálogos de confirmación
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [confirmFinishDialogOpen, setConfirmFinishDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar OTs de ensamblaje
      const { data: woData, error: woError } = await api
        .from('work_orders')
        .select('*')
        .eq('workflow_type', 'assembly')
        .in('status', ['pending', 'in_progress', 'paused'])
        .order('resume_priority', { ascending: false })
        .order('paused_at', { ascending: true, nullsFirst: false })
        .order('created_at');

      if (woError) throw woError;

      // Cargar estanterías
      const { data: shelvesData } = await api
        .from('workshop_shelves')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      setShelves(shelvesData || []);
      setWorkOrders(woData || []);

      // Buscar OT activa del operario
      const active = (woData || []).find(
        (wo: WorkOrder) => wo.status === 'in_progress' && (wo as any).assigned_to === user?.id
      );
      setActiveWO(active || null);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Error cargando datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStartWork = async (wo: WorkOrder) => {
    try {
      const { error } = await api
        .from('work_orders')
        .update({
          status: 'in_progress',
          assigned_to: user?.id,
          started_at: wo.paused_at ? undefined : new Date().toISOString(),
          paused_at: null,
        })
        .eq('id', wo.id);

      if (error) throw error;

      setActiveWO(wo);
      setSelectedWO(wo);
      setWorkNotes(wo.notes || '');
      setWorkDialogOpen(true);

      toast({
        title: wo.paused_at ? 'Trabajo reanudado' : 'Trabajo iniciado',
        description: `OT ${wo.order_number} en progreso`,
      });

      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const handlePause = async () => {
    if (!selectedWO) return;

    setSaving(true);
    try {
      const { error } = await api
        .from('work_orders')
        .update({
          status: 'paused',
          paused_at: new Date().toISOString(),
          resume_priority: 1,
          notes: workNotes || null,
        })
        .eq('id', selectedWO.id);

      if (error) throw error;

      toast({
        title: 'Trabajo pausado',
        description: 'La OT aparecerá como prioritaria mañana',
      });

      setPauseDialogOpen(false);
      setWorkDialogOpen(false);
      setSelectedWO(null);
      setActiveWO(null);
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async () => {
    if (!selectedWO) return;

    setSaving(true);
    try {
      // 1. Marcar OT como completada
      const { error: woError } = await api
        .from('work_orders')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          notes: workNotes || null,
        })
        .eq('id', selectedWO.id);

      if (woError) throw woError;

      // 2. Determinar siguiente paso
      let nextStep = '';
      let nextShelf = '';

      if (selectedWO.requires_paint) {
        // Crear OT de Pintura
        const shelfPaint = shelves.find(s => s.code === 'EST-PINT');
        const { error: paintError } = await api
          .from('work_orders')
          .insert({
            order_number: `${selectedWO.order_number.replace('/ASM', '')}/PNT`,
            product_name: selectedWO.product_name,
            customer: selectedWO.customer,
            quantity: selectedWO.quantity,
            priority: selectedWO.priority,
            status: 'pending',
            workflow_type: 'painting',
            source_shelf_id: shelfPaint?.id,
            parent_work_order_id: selectedWO.parent_work_order_id,
            requires_paint: true,
          });

        if (paintError) throw paintError;
        nextStep = 'OT de Pintura creada';
        nextShelf = 'EST-PINT';
      } else {
        nextStep = 'Pieza lista para despacho';
        nextShelf = 'EST-TERM';
      }

      toast({
        title: 'Trabajo completado',
        description: `${nextStep}. Mover pieza a ${nextShelf}`,
      });

      setFinishDialogOpen(false);
      setConfirmFinishDialogOpen(false);
      setWorkDialogOpen(false);
      setSelectedWO(null);
      setActiveWO(null);
      setWorkNotes('');
      loadData();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Separar OTs por estado con prioridad de pausadas
  const pausedWOs = workOrders.filter(wo => wo.status === 'paused');
  const pendingWOs = workOrders.filter(wo => wo.status === 'pending');
  const inProgressWOs = workOrders.filter(wo => wo.status === 'in_progress');

  const getPausedDuration = (pausedAt: string) => {
    const diff = Date.now() - new Date(pausedAt).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wrench className="h-6 w-6" />
              Estación de Ensamblaje
            </h1>
            <p className="text-muted-foreground">
              Pegado, ajuste y montaje de insertos
            </p>
          </div>
        </div>

        {/* Alerta de pausadas */}
        {pausedWOs.length > 0 && (
          <Card className="border-orange-500 bg-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="h-5 w-5" />
                OTs Pausadas - Prioridad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-orange-600 mb-3">
                Estas OTs fueron pausadas para secado y deben completarse primero
              </p>
              <div className="space-y-2">
                {pausedWOs.map((wo) => (
                  <div
                    key={wo.id}
                    className="flex justify-between items-center p-3 bg-white rounded-lg border"
                  >
                    <div>
                      <p className="font-bold">{wo.order_number}</p>
                      <p className="text-sm">{wo.product_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Pausada hace {getPausedDuration(wo.paused_at!)}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleStartWork(wo)}
                      disabled={!!activeWO}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Reanudar
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* OT Activa */}
        {activeWO && (
          <Card className="border-green-500 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Play className="h-5 w-5" />
                Trabajo en Progreso
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-bold text-lg">{activeWO.order_number}</p>
                  <p>{activeWO.product_name}</p>
                  <p className="text-sm text-muted-foreground">{activeWO.customer}</p>
                </div>
                <Button
                  onClick={() => {
                    setSelectedWO(activeWO);
                    setWorkNotes(activeWO.notes || '');
                    setWorkDialogOpen(true);
                  }}
                >
                  Continuar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending">
              Pendientes ({pendingWOs.length})
            </TabsTrigger>
            <TabsTrigger value="in_progress">
              En Progreso ({inProgressWOs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : pendingWOs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay OTs de ensamblaje pendientes</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>OT</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Prioridad</TableHead>
                        <TableHead>Procesos</TableHead>
                        <TableHead className="text-right">Acción</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingWOs.map((wo) => (
                        <TableRow key={wo.id}>
                          <TableCell>
                            <Badge variant="outline">{wo.order_number}</Badge>
                          </TableCell>
                          <TableCell>{wo.product_name}</TableCell>
                          <TableCell>{wo.customer}</TableCell>
                          <TableCell>
                            <Badge className={PRIORITY_COLORS[wo.priority]}>
                              {wo.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {wo.requires_paint && (
                                <Badge variant="secondary" className="text-xs">
                                  <Palette className="h-3 w-3 mr-1" />
                                  Pintura
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => handleStartWork(wo)}
                              disabled={!!activeWO}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Iniciar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="in_progress">
            <Card>
              <CardContent className="pt-6">
                {inProgressWOs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay OTs en progreso</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>OT</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Acción</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inProgressWOs.map((wo) => (
                        <TableRow key={wo.id}>
                          <TableCell>
                            <Badge variant="outline">{wo.order_number}</Badge>
                          </TableCell>
                          <TableCell>{wo.product_name}</TableCell>
                          <TableCell>{wo.customer}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedWO(wo);
                                setWorkNotes(wo.notes || '');
                                setWorkDialogOpen(true);
                              }}
                            >
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Diálogo de trabajo */}
      <Dialog open={workDialogOpen} onOpenChange={setWorkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>OT de Ensamblaje: {selectedWO?.order_number}</DialogTitle>
            <DialogDescription>
              {selectedWO?.product_name} - {selectedWO?.customer}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Cantidad</Label>
                <p className="font-bold">{selectedWO?.quantity}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Siguiente paso</Label>
                <p className="font-bold flex items-center gap-1">
                  {selectedWO?.requires_paint ? (
                    <>
                      <Palette className="h-4 w-4 text-blue-500" />
                      Pintura
                    </>
                  ) : (
                    'Terminadas'
                  )}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas del trabajo</Label>
              <Textarea
                placeholder="Observaciones, ajustes realizados..."
                value={workNotes}
                onChange={(e) => setWorkNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setPauseDialogOpen(true)}
              className="w-full sm:w-auto"
            >
              <Pause className="h-4 w-4 mr-2" />
              Pausar (Secado)
            </Button>
            <Button
              onClick={() => setFinishDialogOpen(true)}
              className="w-full sm:w-auto"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Finalizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de pausa */}
      <AlertDialog open={pauseDialogOpen} onOpenChange={setPauseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pausar para secado</AlertDialogTitle>
            <AlertDialogDescription>
              La OT quedará marcada como prioritaria y aparecerá destacada cuando
              vuelvas a la estación (por ejemplo, mañana después del secado del pegamento).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePause} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Pausar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Primera confirmación de finalización */}
      <AlertDialog open={finishDialogOpen} onOpenChange={setFinishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar finalización</AlertDialogTitle>
            <AlertDialogDescription>
              Al finalizar, la pieza se moverá a:
              <div className="mt-2 p-3 bg-muted rounded-lg">
                {selectedWO?.requires_paint ? (
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4" />
                    <strong>EST-PINT</strong> (Pintura)
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4" />
                    <strong>EST-TERM</strong> (Terminadas)
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => setConfirmFinishDialogOpen(true)}>
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Segunda confirmación (doble confirmación) */}
      <AlertDialog open={confirmFinishDialogOpen} onOpenChange={setConfirmFinishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmar de nuevo
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que el ensamblaje está completo y la pieza está lista
              para el siguiente proceso?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction onClick={handleFinish} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sí, finalizar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
